import type { EpocusProbeAssociation } from "$global-providers/rxdb-provider/collections/epocus-probe-association";
import { toast } from "$global-providers/toast-provider";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  styled,
  TextField,
  Typography,
} from "@material-ui/core";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditOutlinedIcon from "@material-ui/icons/EditOutlined";
import { type FC, type FormEvent, useState } from "react";
import {
  EPOCUS_BORDER,
  EPOCUS_TEXT_PRIMARY,
  EPOCUS_TEXT_SECONDARY,
  rem,
} from "../../_lib/epocus-palette";
import {
  DEVICE_IDENTITY_HELP,
  PROBE_ASSOCIATION_FAILURE_MESSAGES,
  type ProbeIdentityInput,
  probeIdentityLabel,
  useManageProbeAssociations,
  useMyProbeAssociations,
} from "../../_lib/use-probe-associations";
import { SettingsCard, SettingsCardLabel } from "./settings-card";

const DeviceRow = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 8,
  padding: "8px 0",
  borderBottom: `1px solid ${EPOCUS_BORDER}`,
});

const DeviceValue = styled(Typography)({
  fontSize: rem(14),
  fontWeight: 600,
  color: EPOCUS_TEXT_PRIMARY,
  fontFamily: "monospace",
  wordBreak: "break-all",
});

const DeviceKind = styled(Typography)({
  fontSize: rem(11),
  fontWeight: 700,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: EPOCUS_TEXT_SECONDARY,
});

const IdentityFields = styled(Box)({
  display: "flex",
  flexWrap: "wrap",
  alignItems: "flex-start",
  gap: 8,
  "& > .MuiTextField-root": {
    flex: "1 1 160px",
  },
});

const HelpText = styled(Typography)({
  fontSize: rem(13),
  color: EPOCUS_TEXT_SECONDARY,
});

const HelpList = styled("ul")({
  margin: 0,
  paddingLeft: 18,
  fontSize: rem(13),
  color: EPOCUS_TEXT_SECONDARY,
});

const ActionButton = styled(Button)({
  minHeight: 40,
  textTransform: "none",
  whiteSpace: "nowrap",
});

const IdentityForm: FC<{
  initial: ProbeIdentityInput;
  submitLabel: string;
  saving: boolean;
  autoFocus?: boolean;
  ariaSuffix?: string;
  onSubmit: (identity: ProbeIdentityInput) => Promise<boolean>;
  onCancel?: () => void;
}> = ({
  initial,
  submitLabel,
  saving,
  autoFocus,
  ariaSuffix = "",
  onSubmit,
  onCancel,
}) => {
  const [serial, setSerial] = useState(initial.serial);
  const [aeTitle, setAeTitle] = useState(initial.aeTitle);
  const empty = serial.trim().length === 0 && aeTitle.trim().length === 0;

  return (
    <form
      onSubmit={async (event: FormEvent) => {
        event.preventDefault();
        if (await onSubmit({ serial, aeTitle })) {
          setSerial("");
          setAeTitle("");
        }
      }}
    >
      <IdentityFields>
        <TextField
          autoFocus={autoFocus}
          variant="outlined"
          margin="dense"
          label="Probe serial number"
          value={serial}
          onChange={(event) => setSerial(event.target.value)}
          inputProps={{ "aria-label": `Probe serial number${ariaSuffix}` }}
        />
        <TextField
          variant="outlined"
          margin="dense"
          label="Device AE title"
          value={aeTitle}
          onChange={(event) => setAeTitle(event.target.value)}
          inputProps={{
            "aria-label": `Device AE title${ariaSuffix}`,
            maxLength: 16,
          }}
        />
        <ActionButton
          type="submit"
          variant="contained"
          color="primary"
          disableElevation
          disabled={saving || empty}
          style={{ marginTop: 8 }}
        >
          {saving ? <CircularProgress size={16} /> : submitLabel}
        </ActionButton>
        {onCancel && (
          <ActionButton
            onClick={onCancel}
            disabled={saving}
            style={{ marginTop: 8 }}
          >
            Cancel
          </ActionButton>
        )}
      </IdentityFields>
    </form>
  );
};

const identityOf = (
  association: EpocusProbeAssociation,
): ProbeIdentityInput => ({
  serial: association.deviceSerialNumber,
  aeTitle: association.deviceAeTitle ?? "",
});

export const ProbeAssociationsCard: FC = () => {
  const { associations, loading } = useMyProbeAssociations();
  const { add, update, remove, pendingSerial } = useManageProbeAssociations();
  const [editingId, setEditingId] = useState<string | null>(null);

  const handleAdd = async (identity: ProbeIdentityInput) => {
    const failure = await add(identity);
    if (!failure) {
      toast.success("Device added.");
      return true;
    }
    toast.error(PROBE_ASSOCIATION_FAILURE_MESSAGES[failure]);
    return false;
  };

  const handleUpdate = async (id: string, identity: ProbeIdentityInput) => {
    const failure = await update(id, identity);
    if (!failure) {
      setEditingId(null);
      toast.success("Device updated.");
      return true;
    }
    toast.error(PROBE_ASSOCIATION_FAILURE_MESSAGES[failure]);
    return false;
  };

  const handleRemove = async (id: string) => {
    const failure = await remove(id);
    if (!failure) {
      toast.success("Device removed.");
      return;
    }
    toast.error("Couldn't remove that device. Please try again.");
  };

  return (
    <SettingsCard>
      <SettingsCardLabel>Probes</SettingsCardLabel>
      <HelpText>
        Only scans from these devices appear in your To Document queue. Add
        every device you scan with, by its probe serial number or, for a device
        that sends none, by the AE title set on the device itself. You can share
        a device with colleagues.
      </HelpText>

      {loading && associations.length === 0 ? (
        <Box display="flex" justifyContent="center" py={2}>
          <CircularProgress size={20} aria-label="Loading your probes" />
        </Box>
      ) : associations.length === 0 ? (
        <HelpText>No devices added yet.</HelpText>
      ) : (
        <Box>
          {associations.map((association) => {
            const label = probeIdentityLabel(association);
            return editingId === association.id ? (
              <IdentityForm
                key={association.id}
                initial={identityOf(association)}
                submitLabel="Save"
                saving={pendingSerial !== null}
                autoFocus
                ariaSuffix={` for ${label}`}
                onSubmit={(identity) => handleUpdate(association.id, identity)}
                onCancel={() => setEditingId(null)}
              />
            ) : (
              <DeviceRow key={association.id}>
                <Box>
                  <DeviceValue>{label}</DeviceValue>
                  <DeviceKind>
                    {association.deviceAeTitle
                      ? association.deviceSerialNumber
                        ? `AE title · serial ${association.deviceSerialNumber}`
                        : "AE title"
                      : "Serial number"}
                  </DeviceKind>
                </Box>
                <Box display="flex" style={{ gap: 4 }}>
                  <IconButton
                    size="small"
                    onClick={() => setEditingId(association.id)}
                    aria-label={`Edit device ${label}`}
                  >
                    <EditOutlinedIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleRemove(association.id)}
                    aria-label={`Remove device ${label}`}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Box>
              </DeviceRow>
            );
          })}
        </Box>
      )}

      <IdentityForm
        initial={{ serial: "", aeTitle: "" }}
        submitLabel="Add device"
        saving={pendingSerial !== null}
        onSubmit={handleAdd}
      />

      <HelpText>Where to find it:</HelpText>
      <HelpList>
        {DEVICE_IDENTITY_HELP.map(({ device, where }) => (
          <li key={device}>
            <strong>{device}:</strong> {where}.
          </li>
        ))}
      </HelpList>
    </SettingsCard>
  );
};
